import { call, delay, put, select, takeEvery } from "redux-saga/effects"
import {
  ADD_MENU_FEATURE,
  CLONE_MENU_FEATURE,
  DELETE_MENU_ITEM,
  EDIT_MENU_ITEM,
  GET_FEATURE_DEEPLINK_CODES,
  GET_FEATURE_DEEPLINK_URL,
  GET_MENU_ICONS,
  UPLOAD_MENU_ITEM_IMAGE,
} from "./actionTypes"
import { axiosPut, del, get, post, postImageAxios } from "helpers/api_helper"
import {
  cloneMenuFeatureFailed,
  cloneMenuFeatureSuccess,
  getFeatureDeepLinkUrlFailed,
  getFeatureDeepLinkUrlSuccess,
  getFeatureDeeplinkCodesFailed,
  getFeatureDeeplinkCodesSuccess,
  setMenuFeatureUpdateLoading,
  setSelectedFeature,
  uploadMenuItemImageFailed,
  uploadMenuItemImageSuccess,
} from "./actions"
import {
  addMenuFeatureFailed,
  addMenuFeatureSuccess,
  deleteMenuItemFailed,
  deleteMenuItemSuccess,
  editMenuItemFailed,
  editMenuItemSuccess,
  getMenuIconsFailed,
  getMenuIconsSuccess,
  hideDeleteFeatureModal,
  setIsOutdated,
  updateExperienceSuccess,
  updateLandingSetupSuccess,
} from "store/actions"
import { getFormDataFromJSON, showToast } from "utils"
import * as url from "helpers/url_helper"

function* addMenuFeature({ payload: params }) {
  try {
    const response = yield post(url.MENU_ITEM_URL, params?.data)
    if (params?.type === "event") {
      const data = yield select(state => state.experiences.selectedEvent)
      yield put(
        updateExperienceSuccess({
          ...data,
          design: { ...data?.design, menu: [...data?.design?.menu, response] },
        })
      )
    }
    if (params?.type === "landing") {
      const data = yield select(state => state.LandingScreen.landingSetup)
      yield put(
        updateLandingSetupSuccess({
          ...data,
          menu: [...data?.menu, response],
        })
      )
    }
    yield put(addMenuFeatureSuccess(response))
  } catch (error) {
    yield put(addMenuFeatureFailed(error))
    showToast("error", error, "Error")
  }
}

function* getMenuIcons({ payload: {} }) {
  try {
    const response = yield get(url.GET_MENU_ICONS_URL)
    yield put(getMenuIconsSuccess(response))
  } catch (error) {
    yield put(getMenuIconsFailed(error))
  }
}

function* editMenuItem({ payload: params }) {
  const startTime = Date.now()
  try {
    yield put(setMenuFeatureUpdateLoading(true))
    const response = yield axiosPut(url.MENU_ITEM_URL, params?.data)
    yield put(editMenuItemSuccess(response))
    if (params?.type === "event") {
      const data = yield select(state => state.experiences.selectedEvent)
      yield put(
        updateExperienceSuccess({
          ...data,
          design: {
            ...data?.design,
            menu: data?.design?.menu?.map(x =>
              x?.id == params?.data?.menu_item_id ? response : x
            ),
          },
        })
      )
    }
    if (params?.type === "landing") {
      const data = yield select(state => state.LandingScreen.landingSetup)
      yield put(
        updateLandingSetupSuccess({
          ...data,
          menu: data?.menu?.map(x =>
            x?.id == params?.data?.menu_item_id ? response : x
          ),
        })
      )
    }
    yield put(
      setSelectedFeature(response)
    )
    const elapsedTime = Date.now() - startTime
    const minLoadingTime = 1000
    if (elapsedTime < minLoadingTime) {
      yield delay(minLoadingTime - elapsedTime)
    }
  } catch (error) {
    yield put(editMenuItemFailed(error))
    showToast("error", "Failed to update Menu Feature", "Error")
  } finally {
    yield put(setMenuFeatureUpdateLoading(false))
  }
}

function* deleteMenuItem({ payload: params }) {
  try {
    yield del(url.MENU_ITEM_URL, { menu_item_id: params?.menu_item_id })
    yield put(deleteMenuItemSuccess(params?.menu_item_id))
    if (params?.type === "event") {
      const data = yield select(state => state.experiences.selectedEvent)
      yield put(
        updateExperienceSuccess({
          ...data,
          design: {
            ...data?.design,
            menu: data?.design?.menu?.filter(
              x => x?.id !== params?.menu_item_id
            ),
          },
        })
      )
    }
    if (params?.type === "landing") {
      const data = yield select(state => state.LandingScreen.landingSetup)
      yield put(
        updateLandingSetupSuccess({
          ...data,
          menu: data?.menu?.filter(x => x?.id !== params?.menu_item_id),
        })
      )
    }
    yield put(hideDeleteFeatureModal())
  } catch (error) {
    yield put(deleteMenuItemFailed(error))
    console.log("ERROR", error)
    showToast("error", "Failed to delete Menu Feature", "Error")
  }
}

function* uploadMenuItemImage({ payload: params }) {
  try {
    const fileResponse = yield postImageAxios(
      "/file",
      getFormDataFromJSON(params.image)
    )
    const response = yield axiosPut("/admin/menu-item", {
      ...params.menuItem,
      image_file: fileResponse[0],
    })
    yield put(uploadMenuItemImageSuccess(response))
    yield put(setIsOutdated(true))
  } catch (error) {
    yield put(uploadMenuItemImageFailed(error))
  }
}

function* getFeatureDeepLinkUrl({ payload: params }) {
  try {
    const response = yield get(`/admin/event/${params?.eventId}/deeplink-url`, {
      params: {
        menu_item_id: params?.featureId,
      },
    })
    yield put(getFeatureDeepLinkUrlSuccess(response))
  } catch (error) {
    yield put(getFeatureDeepLinkUrlFailed(error))
    showToast("error", error, "Error")
  }
}

function* getFeatureDeeplinkCodes({ payload: params }) {
  try {
    const response = yield get(url.DEEPLINK_CODES_URL, { params })
    yield put(getFeatureDeeplinkCodesSuccess(response))
  } catch (error) {
    yield put(getFeatureDeeplinkCodesFailed(error))
    showToast("error", error, "Error")
  }
}

function* cloneMenuFeature({ payload: params }) {
  try {
    const { entity_id, ...rest } = params?.data
    const response = yield post(`/admin/feature/${entity_id}/clone`, rest)
    yield put(setIsOutdated(true))
    if (params?.callback) {
      yield call(params?.callback)
    }
    yield put(cloneMenuFeatureSuccess(response))
    showToast("success", "Menu feature cloned", "Success")
  } catch (error) {
    yield put(cloneMenuFeatureFailed(error))
    showToast("error", error, "Error")
  }
}

function* menuFeaturesSaga() {
  yield takeEvery(ADD_MENU_FEATURE, addMenuFeature)
  yield takeEvery(GET_MENU_ICONS, getMenuIcons)
  yield takeEvery(EDIT_MENU_ITEM, editMenuItem)
  yield takeEvery(DELETE_MENU_ITEM, deleteMenuItem)
  yield takeEvery(UPLOAD_MENU_ITEM_IMAGE, uploadMenuItemImage)
  yield takeEvery(GET_FEATURE_DEEPLINK_URL, getFeatureDeepLinkUrl)
  yield takeEvery(GET_FEATURE_DEEPLINK_CODES, getFeatureDeeplinkCodes)
  yield takeEvery(CLONE_MENU_FEATURE, cloneMenuFeature)
}

export default menuFeaturesSaga
